'use client'
import {Button, ButtonSpinner, Checkbox, Form, Input, Locale, Slideout} from '@local/ui/components'
import {cx} from '@local/utils/cx'

import CloseSVG from '@local/assets/svgs/icon-close.svg'
import {useRef, useState} from 'react'
import {EMAIL_PATTERN, emailIsValid} from '@local/utils/string'
import {useTranslate} from '@local/hooks'
import ContactForm from '@local/ui/components/flodesk/ContactForm'
import {post} from '@local/utils/fetch'
import {usePathname} from 'next/navigation'
interface FlodeskFormProps {
	buttonText?: string
	formTitle?: string
	flodeskID: string
	successText?: string
	successTitle?: string
	extraQuestions?: {
		question: string
	}[]

}

export const FlodeskForm = ({flodeskID, extraQuestions, successText = 'Thank you for your message', successTitle = 'Thank you for your message', buttonText = 'Subscribe', formTitle}: FlodeskFormProps) => {
	const locale = useTranslate()
	const [isOpen, setIsOpen] = useState<boolean>(false)
	const pathname = usePathname()
	const extraQuestionsArray = extraQuestions?.map((question) => ({
		question: question.question,
		fieldName: question.question.toLowerCase().replace(/ /g, '').replace('?', ''),
	}))

	const [inputs, setInputs] = useState({
		firstname: '',
		lastname: '',
		email: '',
		subscribeMail: false,
		...extraQuestionsArray?.reduce((acc, question) => ({...acc, [question.fieldName]: ''}), {}),
	})
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [isSuccess, setIsSuccess] = useState<boolean>(false)
	const [hasError, setHasError] = useState<boolean>(false)

	const formRef = useRef<HTMLFormElement>(null)

	const [allowSubscribe, setAllowSubscribe] = useState<boolean>(true)
	const buttonRef = useRef<HTMLButtonElement>(null)
	const [subscribtionStatus, setSubscribtionStatus] = useState<string>('default')

	const handleInput = (value: string, name: string) => {
		setInputs((prev) => ({...prev, [name]: value}))
	}

	const handleReset = () => {
		setInputs({
			firstname: '',
			lastname: '',
			email: '',
			subscribeMail: false,
			...extraQuestionsArray?.reduce((acc, question) => ({...acc, [question.fieldName]: ''}), {}),
		})
	}

	const handleSubmit = async ({body}) => {
		setIsLoading(true)
		try {
			setIsSuccess(false)
			setHasError(false)

			if (!emailIsValid(body.email)) {
				throw new Error('InvalidEmail')
			}

			const res = await fetch('/api/form/', {
				method: 'POST',
				body: JSON.stringify({
					emailAddress: body.email,
					firstName: body.firstname,
					lastName: body.lastname,
					formName: formTitle,
					isSubscribed: body.subscribeMail,
					extraQuestionString: extraQuestionsArray?.map((question) => `${question.question}: ${body[question.fieldName]}`).join('\n'),
				}),
			})
			const result = await res.json()
			if (res.status === 200) {
				post('/api/fb-conversion', {
					event: 'join-waitinglist',
					pathname,
					data: {
						firstName: body.firstName,
						lastName: body.lastName,
						email: body.email,
						subscribeMail: body.subscribeMail ? 'yes' : 'no',
					},
				})
			}
			if (res.status === 200) {
				if (body.subscribeMail) {
					buttonRef.current?.click()
				}
				setIsSuccess(true)
				setIsLoading(false)
				return {status: 200}
			} else {
				setIsLoading(false)
				setHasError(true)
				return {errors: [result]}
			}
		} catch (error) {
			setIsLoading(false)
			setHasError(true)
		}
	}
	return (
		<>
			<Button
				small
				variant='primary'
				onClick={() => setIsOpen(true)}
				border>
				{buttonText || locale('Subscribe')}
			</Button>
			<Slideout
				variant='right'
				isOpen={isOpen}
				onClickClose={() => setIsOpen(false)}
				className='bg-grey-4 text-white relative !px-0'>
				<div data-lenis-prevent className='overflow-y-scroll pb-40 min-h-screen h-fit margin-px-1 flex flex-col scrollbar-hide'>
					<div className='sticky top-0 z-6 flex items-center justify-between w-full py-10 lg:py-20 bg-grey-4'>
						<h2 className='w-fit uppercase'>{formTitle || locale('Have a question?')}</h2>
						<Button
							small
							variant='icon'
							onClick={() => setIsOpen(false)}
							className='px-16 aspect-square'>
							<CloseSVG />
						</Button>
					</div>
					<Form
						ref={formRef}
						autoReset
						additionalReset={handleReset}
						onSubmit={handleSubmit}
						hideSuccess={subscribtionStatus === 'default'}
						successMessage={<SuccessPanel title={successTitle} text={successText} />}
						errorClassName='bottom-8 text-center'
						successClassName='z-5 mt-0 -inset-1 bg-grey-4 w-[calc(100%+2px)]'
						className={cx(
							'flex flex-col h-fit w-full pt-40 gap-50',
							'[&_h3]:font-normal [&_h3]:text-18',
						)}>

						<div className='grid grid-cols-2 gap-20'>
							<Input
								required
								variant='dark'
								type='text'
								name='firstname'
								label={locale('Firstname')}
								value={inputs.firstname}
								onChange={e => handleInput(e.target.value, 'firstname')}
								className='h-48'
								inputClassName='h-full'
								errorClassName='z-10 bg-grey-4'
								labelClassName='bg-grey-4' />
							<Input
								required
								variant='dark'
								type='text'
								name='lastname'
								label={locale('Lastname')}
								value={inputs.lastname}
								onChange={e => handleInput(e.target.value, 'lastname')}
								className='h-48'
								inputClassName='h-full'
								errorClassName='z-10 bg-grey-4'
								labelClassName='bg-grey-4' />
							<Input
								required
								variant='dark'
								type='email'
								name='email'
								label='Email'
								value={inputs.email}
								onChange={e => handleInput(e.target.value, 'email')}
								onInvalid={() => setHasError(true)}
								pattern={EMAIL_PATTERN}
								hasError={hasError}
								className='h-48 col-span-2'
								inputClassName='h-full'
								errorClassName='z-10 bg-grey-4'
								labelClassName='bg-grey-4' />

							{extraQuestionsArray?.map((question, index) => (
								<Input
									key={index}
									variant='dark'
									type='text'
									name={question.fieldName}
									required
									label={question.question}
									value={inputs[question.fieldName]}
									onChange={e => handleInput(e.target.value, question.fieldName)}
									className='h-60 col-span-2'
									inputClassName='h-full'
									errorClassName='z-10 bg-grey-4'
									labelClassName='bg-grey-4 text-left' />
							))}
							<Checkbox
								checked={allowSubscribe} onChange={() => {
									setAllowSubscribe(!allowSubscribe)
									setSubscribtionStatus('notAllowed')
								}} name='subscribeMail' theme='dark' className='col-span-2'>
								<span className='text-12 text-white'>Receive inspiration in your inbox</span>
							</Checkbox>
						</div>

						<div className='pb-25 w-full'>
							<ButtonSpinner
								type='submit'
								small
								loading={isLoading}
								variant='primary-transparent'
								className='mt-auto w-full'>
								<Locale>Send</Locale>
							</ButtonSpinner>
						</div>
					</Form>
					{subscribtionStatus !== 'notAllowed' &&
						<ContactForm
							buttonRef={buttonRef}
							firstName={inputs.firstname}
							lastName={inputs.lastname}
							email={inputs.email}
							segmentID={flodeskID}
							setStatus={setSubscribtionStatus}
							privateExperience />}

				</div>
			</Slideout>
		</>
	)
}

const SuccessPanel = ({title, text}) => (
	<div className='flex flex-col items-start pt-40 w-full h-fit gap-14 bg-grey-4 normal-case text-left sticky top-86'>
		<h3 className='text-32 font-serif tracking-tight'>{title}</h3>
		<p className='text-12 text-grey-1 lg:span-mr-1-wide'>{text}</p>
	</div>
)
